import React, { useEffect, useState } from 'react';
import {
  acceptCallByToken,
  cancelCallByToken,
  counterCallByToken,
  declineCallByToken,
  getCallByToken,
} from '../../../api/v2/calls';
import { formatScheduledAt } from '../../../helpers/timezone_utils';
import { buildCallEventCardProps } from '../../users/conversation_room/call_event_card/build_props';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import AvailabilityPicker from '../availability_picker';
import { PhoneIcon } from '../call_page_icon';
import CallEventCard from '../../users/conversation_room/call_event_card';

import styles from './index.module.css';

function firstName(name) {
  if (!name) return '';
  return name.trim().split(' ')[0];
}

const CallSchedulePicker = ({ match }) => {
  const { token } = match.params;
  const [callInfo, setCallInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [isCanceling, setIsCanceling] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    getCallByToken(token)
      .then((data) => setCallInfo(data))
      .catch(() => setCallInfo(false))
      .finally(() => setInfoLoading(false));
  }, [token]);

  if (infoLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.status}>Loading call…</p>
      </div>
    );
  }

  if (callInfo === false) {
    return (
      <div className={styles.page}>
        <CallEventCard
          tone='declined'
          title='Link unavailable'
          description='This link is invalid or has expired. Please check the URL and try again.'
        />
      </div>
    );
  }

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const viewerTimezone = callInfo.viewer_timezone || browserTimezone;
  const isMentor = callInfo.viewer_profile_type === 'PatientPartner';
  const otherFirstName = firstName(callInfo.other_user_name);

  const handleAcceptSlot = (slot) => {
    acceptCallByToken(token, slot.start_at)
      .then((updatedCall) => setCallInfo(updatedCall))
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'));
  };

  const handleAccept = () => {
    acceptCallByToken(token)
      .then((updatedCall) => setCallInfo(updatedCall))
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'));
  };

  const handleCancel = () => {
    setIsCanceling(true);
    const isMentorDecliningIncoming = isMentor && callInfo.status === 'pending' && !callInfo.countered_at;
    const request = isMentorDecliningIncoming ? declineCallByToken(token) : cancelCallByToken(token);

    request
      .then((updatedCall) => setCallInfo(updatedCall))
      .catch((err) => failToast(err.message || 'Could not update this call. Please try again.'))
      .finally(() => setIsCanceling(false));
  };

  const cardProps = buildCallEventCardProps({
    call: callInfo,
    isMentor,
    viewerTimezone,
    otherUserFirstName: otherFirstName,
    onAcceptSlot: (slot) => setPending({ kind: 'slot', slot }),
    onAccept: () => setPending({ kind: 'accept' }),
    onCancel: handleCancel,
    onCounter: () => setPending({ kind: 'counter' }),
  });

  if (pending) {
    const slotLabel = pending.slot ? formatScheduledAt(pending.slot.start_at, viewerTimezone) : null;
    const confirmCopy = {
      slot: {
        title: 'Accept this time?',
        description: slotLabel
          ? `Accept ${slotLabel}. Nothing is confirmed until you say yes.`
          : 'Nothing is confirmed until you say yes.',
        label: 'Yes, accept this time',
      },
      accept: {
        title: 'Accept this call?',
        description: 'Nothing is confirmed until you say yes.',
        label: 'Yes, accept the call',
      },
      counter: {
        title: 'Propose new times?',
        description: 'You can pick different times. Nothing is sent until you submit them.',
        label: 'Yes, pick times',
      },
    }[pending.kind];

    const confirmPending = () => {
      if (pending.kind === 'slot') handleAcceptSlot(pending.slot);
      if (pending.kind === 'accept') handleAccept();
      if (pending.kind === 'counter') setProposing(true);
      setPending(null);
    };

    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <h1 className={styles.title}>{confirmCopy.title}</h1>
          <p className={styles.description}>{confirmCopy.description}</p>
          <button type='button' className={`${styles.btn} ${styles.btn_success}`} onClick={confirmPending}>
            <PhoneIcon size={16} />
            {confirmCopy.label}
          </button>
          <button type='button' className={`${styles.btn} ${styles.btn_secondary}`} onClick={() => setPending(null)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {cardProps ? (
        <CallEventCard {...cardProps} isCanceling={isCanceling} />
      ) : (
        <p className={styles.status}>This call does not have times to choose from.</p>
      )}
      {proposing && (
        <AvailabilityPicker
          mentorTimezone={viewerTimezone}
          viewerTimezone={viewerTimezone}
          mentorFirstName={otherFirstName}
          otherUserName={callInfo.other_user_name}
          otherUserPhoto={callInfo.other_user_photo}
          submitProposedSlots={(proposedSlots) => counterCallByToken(token, proposedSlots)}
          onClose={() => setProposing(false)}
          onCallCreated={(updatedCall) => {
            if (updatedCall) setCallInfo(updatedCall);
            setProposing(false);
          }}
        />
      )}
    </div>
  );
};

export default CallSchedulePicker;
